import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { ApprovalObserverTypeDisplayOptions } from '@app/types/translatables/time-off-v3-display.options';
import { BaseForm } from '@forms/base.form';
import { Employee } from '@models/employee/employee.model';
import { TimeOffApprovalFlowStep } from '@models/time-off-v3/time-off-approval-flow-step.model';
import { TimeOffApprovalFlow } from '@models/time-off-v3/time-off-approval-flow.model';
import { TimeOffApproverType } from '@models/time-off-v3/time-off-approver-type.model';
import { TimeOffApprover } from '@models/time-off-v3/time-off-approver.model';

@Component({
    selector: 'app-approval-flow-steps-form',
    templateUrl: './approval-flow-steps.template.html',
    styleUrls: ['./approval-flow-steps.style.scss'],
})
export class ApprovalFlowStepsForm extends BaseForm implements OnInit {
    @Input() approvalFlow: TimeOffApprovalFlow;
    @ViewChild('form') form: NgForm;

    initialApprovalFlowSteps: TimeOffApprovalFlowStep[] = [];
    addedApprovalFlowSteps: TimeOffApprovalFlowStep[] = [];
    deletedApprovalFlowSteps: TimeOffApprovalFlowStep[] = [];
    approverTypes: TimeOffApproverType[] = [];
    specificPersonApproverTypeId: number;
    isLoading = false;

    approvalObserverTypeDisplay = ApprovalObserverTypeDisplayOptions;

    get approvalFlowSteps(): TimeOffApprovalFlowStep[] {
        return [...this.initialApprovalFlowSteps, ...this.addedApprovalFlowSteps];
    }

    isApproverTypeUsed(approverTypeId: number): boolean {
        return (
            this.approvalFlowSteps
                .map((approver: TimeOffApprovalFlowStep) => approver.timeOffApproverTypeId)
                .filter((id: number) => id !== this.specificPersonApproverTypeId)
                .findIndex((id: number) => id === approverTypeId) > -1
        );
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;

        const [approverTypes] = await TimeOffApproverType.get();

        const [approvalFlowSteps] = await TimeOffApprovalFlowStep.with('timeOffApprover.approver')
            .where('timeOffApprovalFlowId', this.approvalFlow.id)
            .orderBy('stepIndex', 'ASC')
            .get();
        const [specificApproverType] = approverTypes.filter((approverType: TimeOffApproverType) =>
            approverType.isSpecificPerson()
        );

        this.approverTypes = approverTypes;
        this.initialApprovalFlowSteps = approvalFlowSteps;
        this.specificPersonApproverTypeId = specificApproverType?.id;

        this.isLoading = false;
    }

    addApprovalFlowStep(): void {
        const [firstAvailableApproverType] = this.approverTypes.filter(
            (approverType: TimeOffApproverType) => !this.isApproverTypeUsed(approverType.id)
        );

        this.addedApprovalFlowSteps.push(
            new TimeOffApprovalFlowStep({
                timeOffApproverTypeId: firstAvailableApproverType.id,
                timeOffApprovalFlowId: this.approvalFlow.id,
            })
        );
    }

    removeApprovalFlowStep(approvalFlowStep: TimeOffApprovalFlowStep): void {
        if (approvalFlowStep.id) {
            this.deletedApprovalFlowSteps.push(approvalFlowStep);
            const removalIndex = this.initialApprovalFlowSteps.findIndex(
                (_approvalFlowStep: TimeOffApprovalFlowStep) => _approvalFlowStep.id === approvalFlowStep.id
            );
            this.initialApprovalFlowSteps.splice(removalIndex, 1);
            return;
        }

        const removalIndex = this.addedApprovalFlowSteps.findIndex(
            (_approver: TimeOffApprovalFlowStep) => _approver === approvalFlowStep
        );
        this.addedApprovalFlowSteps.splice(removalIndex, 1);
    }

    setApproverId(employee: Employee, approvalFlowStep: TimeOffApprovalFlowStep): void {
        approvalFlowStep.approverId = employee.id;
    }

    wipeSpecificPerson(event: MatSelectChange, approvalFlowStep: TimeOffApprovalFlowStep): void {
        if (event.value === this.specificPersonApproverTypeId) {
            return;
        }

        approvalFlowStep.approverId = null;
    }

    getApprover(timeOffApprover: TimeOffApprover): Employee | null {
        if (!timeOffApprover) {
            return null;
        }

        return timeOffApprover.approver;
    }
}
